import { useMemo, useState } from 'react'
import { Gauge, Eye, Check } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../lib/shadcn/table'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { cn } from '../../lib/shadcn/utils'
import { Chip } from '../interviews/Chip'
import { useMarkKmSeen } from '../../hooks/backend/shifts'
import { formatNum, formatDate } from '../../utils/interviews'
import { QueueFilters, KM_FILTERS, type QueueFilter } from './QueueFilters'
import { NotifyDriverDialog } from './NotifyDriverDialog'
import { proposalCategory, windowLabel, type ReconcileProposal } from './reconcile'

export type KmDiscrepancyRow = {
  shift_id: string
  shift_date: string | null
  shift_window: string | null
  driver_id: string | null
  driver_name: string | null
  vehicle_id: string | null
  registration_number: string | null
  declared_km: number | string | null
  cartrack_km: number | string | null
  gap_km: number | string | null
  understatement_pct: number | string | null
  validation_status: string | null
  earnings_as_paid: number | string | null
  created_at: string | null
  seen: boolean | null
  seen_at: string | null
  driver_notified: boolean | null
  notified_at: string | null
}

function humanize(value: string | null | undefined): string {
  if (!value) return '—'
  const spaced = value.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

// The per-row "Seen" affordance: a button until the row is marked seen, then a
// quiet chip carrying the date it was seen.
function SeenControl({ row, onSeen }: { row: KmDiscrepancyRow; onSeen: () => void }) {
  const { trigger, loading } = useMarkKmSeen()
  const [busy, setBusy] = useState(false)

  if (row.seen === true) {
    return (
      <Chip tone="neutral" icon={<Eye className="h-3 w-3" />}>
        Seen {formatDate(row.seen_at)}
      </Chip>
    )
  }

  async function handleSeen() {
    setBusy(true)
    try {
      await trigger({ shift_id: row.shift_id, notes: null }, { skipCache: true }).result
      toast.success('Marked as seen.')
      onSeen()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const working = busy || loading
  return (
    <Button
      variant="outline"
      size="sm"
      className="h-7 gap-1.5 px-2 text-xs"
      disabled={working}
      onClick={handleSeen}
    >
      {working ? <Spinner className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      Seen
    </Button>
  )
}

function matchesFilter(
  row: KmDiscrepancyRow,
  proposal: ReconcileProposal | undefined,
  filter: QueueFilter,
): boolean {
  switch (filter) {
    case 'all':
      return true
    case 'unseen':
      return row.seen !== true
    case 'seen':
      return row.seen === true
    default:
      return proposalCategory(proposal) === filter
  }
}

export function KmDiscrepancyQueue({
  rows,
  proposals,
  filter,
  onFilterChange,
  onChanged,
}: {
  rows: KmDiscrepancyRow[]
  proposals: Map<string, ReconcileProposal>
  filter: QueueFilter
  onFilterChange: (next: QueueFilter) => void
  // Refresh the queue after a row is marked seen or a driver is notified.
  onChanged: () => void
}) {
  const counts = useMemo(() => {
    const c: Partial<Record<QueueFilter, number>> = {}
    for (const f of KM_FILTERS) {
      c[f.id] = rows.filter((r) => matchesFilter(r, proposals.get(r.shift_id), f.id)).length
    }
    return c
  }, [rows, proposals])

  const visible = useMemo(
    () => rows.filter((r) => matchesFilter(r, proposals.get(r.shift_id), filter)),
    [rows, proposals, filter],
  )

  return (
    <div className="space-y-3">
      <QueueFilters filters={KM_FILTERS} value={filter} counts={counts} onChange={onFilterChange} />

      {visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 p-12 text-center text-sm text-muted-foreground">
          <Gauge className="h-8 w-8 opacity-40" />
          No kilometre discrepancies match this filter.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Shift</TableHead>
                <TableHead>Driver</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead className="text-right">Declared km</TableHead>
                <TableHead className="text-right">Cartrack km</TableHead>
                <TableHead className="text-right">Gap km</TableHead>
                <TableHead className="text-right">Understated</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((r) => {
                const pct = r.understatement_pct == null ? null : Number(r.understatement_pct)
                const high = pct != null && pct > 25
                const seen = r.seen === true
                return (
                  <TableRow key={r.shift_id} className={cn(seen && 'opacity-60')}>
                    <TableCell>
                      <div className="text-sm font-medium text-foreground">
                        {formatDate(r.shift_date)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {windowLabel(r.shift_window)}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm font-medium text-foreground">
                      {r.driver_name || 'Unnamed driver'}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-foreground">
                      {r.registration_number || '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {formatNum(r.declared_km, 0)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {formatNum(r.cartrack_km, 0)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {formatNum(r.gap_km, 0)}
                    </TableCell>
                    <TableCell
                      className={cn(
                        'text-right tabular-nums font-medium',
                        high ? 'text-rose-600 dark:text-rose-400' : 'text-foreground',
                      )}
                    >
                      {pct == null ? '—' : `${formatNum(pct, 1)}%`}
                    </TableCell>
                    <TableCell>
                      <Chip tone="neutral">{humanize(r.validation_status)}</Chip>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <SeenControl row={r} onSeen={onChanged} />
                        {r.driver_notified === true ? (
                          <Chip tone="green" icon={<Check className="h-3 w-3" />}>
                            Notified {formatDate(r.notified_at)}
                          </Chip>
                        ) : (
                          <NotifyDriverDialog
                            shiftId={r.shift_id}
                            driverName={r.driver_name}
                            shiftDate={r.shift_date}
                            declaredKm={r.declared_km}
                            cartrackKm={r.cartrack_km}
                            onNotified={onChanged}
                          />
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
